/**
 * Parser for the ВУСО / LILO coverage `.xlsx` workbook. Each diagnosis sheet becomes one parsed
 * option (the sheet name is the option name) carrying the covered diagnoses found on that sheet.
 *
 * A `.xlsx` file is a ZIP of XML parts; we unzip with `fflate` and read the parts with the browser's
 * `DOMParser` — no heavyweight spreadsheet dependency. Only the two columns we need are interpreted.
 *
 * Sheet layout (all diagnosis sheets share it):
 *   | A: Діагноз (group code) | B: Опис | C: Діагноз (деталізований) | D: Опис |
 * Group rows carry the code in column A (skipped); the lowest-level diagnoses carry the code in
 * column B and the name in column C. A sheet is treated as a diagnosis sheet only when its header row
 * matches this layout — the meta sheets (Зміст, Легенда) are ignored.
 */
import { strFromU8, unzipSync } from 'fflate';

import { Diagnosis } from './diagnosis.model';

/** One diagnosis sheet parsed into an importable option. */
export interface ParsedCoverageOption {
  /** Source sheet name — used as the option name. */
  name: string;
  diagnoses: Diagnosis[];
}

const HEADER_GROUP_CODE = 'Діагноз';
const HEADER_DETAILED_CODE = 'Діагноз (деталізований)';

/** Parses a coverage workbook (as an ArrayBuffer) into one option per diagnosis sheet. */
export function parseCoverageWorkbook(buffer: ArrayBuffer): ParsedCoverageOption[] {
  const files = unzipSync(new Uint8Array(buffer));
  const sharedStrings = readSharedStrings(files);
  const sheets = readWorkbookSheets(files);
  const options: ParsedCoverageOption[] = [];

  for (const sheet of sheets) {
    const part = files[sheet.path];
    if (!part) continue;
    const rows = readSheetRows(part, sharedStrings);
    if (!isDiagnosisSheet(rows)) continue;

    options.push({ name: sheet.name, diagnoses: extractDiagnoses(rows) });
  }

  return options;
}

interface SheetRef {
  name: string;
  /** Path of the worksheet XML part inside the archive (e.g. `xl/worksheets/sheet3.xml`). */
  path: string;
}

/** Resolves the ordered list of sheets to their worksheet XML parts via the workbook relationships. */
function readWorkbookSheets(files: Record<string, Uint8Array>): SheetRef[] {
  const workbook = files['xl/workbook.xml'];
  const rels = files['xl/_rels/workbook.xml.rels'];
  const relTargets = new Map<string, string>();
  const sheets: SheetRef[] = [];

  if (!workbook || !rels) return [];

  for (const rel of Array.from(parseXml(rels).getElementsByTagName('Relationship'))) {
    const id = rel.getAttribute('Id');
    const target = rel.getAttribute('Target');

    if (id && target) relTargets.set(id, target.replace(/^\/?xl\//, '').replace(/^\//, ''));
  }

  for (const sheet of Array.from(parseXml(workbook).getElementsByTagName('sheet'))) {
    const name = sheet.getAttribute('name');
    const relId = sheet.getAttribute('r:id') ?? sheet.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id');
    const target = relId ? relTargets.get(relId) : undefined;

    if (name && target) sheets.push({ name, path: `xl/${target}` });
  }

  return sheets;
}

/** Reads the shared-string table; each entry concatenates all of its text runs. */
function readSharedStrings(files: Record<string, Uint8Array>): string[] {
  const part = files['xl/sharedStrings.xml'];

  if (!part) return [];
  else return Array.from(parseXml(part).getElementsByTagName('si')).map(textOf);
}

/** Reads a worksheet into a dense grid of cell values, indexed as `rows[rowIndex][columnIndex]`. */
function readSheetRows(part: Uint8Array, sharedStrings: string[]): (string | number | null)[][] {
  const rows: (string | number | null)[][] = [];

  for (const rowNode of Array.from(parseXml(part).getElementsByTagName('row'))) {
    const cells: (string | number | null)[] = [];

    for (const cell of Array.from(rowNode.getElementsByTagName('c'))) {
      const column = columnIndex(cell.getAttribute('r'));

      if (column < 0) continue;
      cells[column] = cellValue(cell, sharedStrings);
    }

    rows.push(cells);
  }
  return rows;
}

/** Resolves a cell's value, dereferencing shared strings and inline strings; numbers stay numeric. */
function cellValue(cell: Element, sharedStrings: string[]): string | number | null {
  const type = cell.getAttribute('t');
  const raw = cell.getElementsByTagName('v')[0]?.textContent;
  const numeric = Number(raw);

  if (type === 's') {
    const index = Number(cell.getElementsByTagName('v')[0]?.textContent ?? '');
    return sharedStrings[index] ?? null;
  }
  if (type === 'inlineStr') return textOf(cell) || null;
  else if (type === 'str') return cell.getElementsByTagName('v')[0]?.textContent ?? null;
  else if (raw === undefined || raw === null || raw === '') return null;
  else return Number.isNaN(numeric) ? raw : numeric;
}

/** True when the first row is the shared diagnosis-sheet header (Діагноз | Опис | Діагноз (деталізований)). */
function isDiagnosisSheet(rows: (string | number | null)[][]): boolean {
  const header = rows[0] ?? [];

  return text(header[0]) === HEADER_GROUP_CODE && text(header[2]) === HEADER_DETAILED_CODE;
}

/** Collects the lowest-level diagnoses: rows whose column B holds a code (name in column C), deduped by code. */
function extractDiagnoses(rows: (string | number | null)[][]): Diagnosis[] {
  const byCode = new Map<string, Diagnosis>();

  for (let i = 1; i < rows.length; i++) {
    const cell = rows[i]?.[1];

    if (typeof cell !== 'string') continue;
    const code = cell.trim();

    if (!code) continue;
    if (!byCode.has(code)) byCode.set(code, { code, name: text(rows[i]?.[2]) });
  }

  return [...byCode.values()];
}

/** Converts a cell reference's column letters (`A`, `B`, `AA`, …) to a zero-based index; -1 when absent. */
function columnIndex(ref: string | null): number {
  if (!ref) return -1;

  const letters = ref.match(/^[A-Z]+/)?.[0];
  let index = 0;

  if (!letters) return -1;

  for (const char of letters) index = index * 26 + (char.charCodeAt(0) - 64);

  return index - 1;
}

function parseXml(part: Uint8Array): Document {
  return new DOMParser().parseFromString(strFromU8(part), 'application/xml');
}

/** Concatenated text of all descendant `<t>` nodes (shared/inline strings may split into runs). */
function textOf(node: Element): string {
  return Array.from(node.getElementsByTagName('t'))
    .map(t => t.textContent ?? '')
    .join('');
}

function text(value: string | number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value).trim();
}
